// A superset of what the image tool stores (_EXTENSION_BY_IMAGE_MIME in the
// backend's tools/image_generation/impl.py): other session files, such as a
// Code Interpreter chart, can also be svg.
const IMAGE_MIME_BY_EXTENSION: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  svg: "image/svg+xml",
}

/** Whether `extension` (lower-case, no dot) is one the image tools write. */
export function isImageFileExtension(extension: string): boolean {
  return Object.hasOwn(IMAGE_MIME_BY_EXTENSION, extension)
}

/** Extension of a stored image file, lower-cased, without the dot; "" when none. */
export function imageFileExtension(filename: string): string {
  return filename.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? ""
}

/**
 * MIME type for the blob built from a stored image's bytes, from the file's
 * extension (the image tool chooses it from the provider's MIME type; Code
 * Interpreter files keep their own). Unknown extensions fall back to PNG, the
 * image tool's default; no writer produces one today.
 */
export function imageMimeTypeForFilename(filename: string): string {
  return IMAGE_MIME_BY_EXTENSION[imageFileExtension(filename)] ?? "image/png"
}

// The inverse of IMAGE_MIME_BY_EXTENSION (first extension wins, so jpeg maps
// to "jpg"), plus the non-standard "image/jpg" alias some providers send.
const EXTENSION_BY_IMAGE_MIME: Record<string, string> = {
  "image/jpg": "jpg",
  ...Object.fromEntries(
    Object.entries(IMAGE_MIME_BY_EXTENSION)
      .reverse()
      .map(([extension, mime]) => [mime, extension])
  ),
}

/** File extension for a `data:image/...` URL's MIME type; "" when it has none we know. */
export function imageExtensionFromDataUrl(url: string): string {
  const mime = url.match(/^data:([^;,]+)/)?.[1]?.toLowerCase() ?? ""
  return EXTENSION_BY_IMAGE_MIME[mime] ?? ""
}
